import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-accent text-accent-foreground hover:bg-accent/90 uppercase tracking-wide",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-silver bg-transparent text-foreground hover:bg-card hover:shadow-[0_0_20px_hsl(var(--gold)/0.3)]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-card hover:text-accent",
        link: "text-accent underline-offset-4 hover:underline",
        premium: "bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-lg hover:shadow-[var(--shadow-gold-glow)] transition-all duration-300 uppercase tracking-wide",
        hero: "bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base px-8 py-6 shadow-[var(--shadow-gold)] hover:shadow-[var(--shadow-gold-glow)] transition-all duration-300 uppercase tracking-wider",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
